import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Hook: Bidirectional location sync between the app and GHL CRM.
 * - GHL → App: When GHL location changes, auto-switches the app's current location.
 * - App → GHL: When app location changes, sends postMessage to parent to switch GHL sub-account.
 * - Access Control: If user doesn't have access to a location, blocks the UI.
 */
export function useGhlLocationSync() {
  const {
    locations, currentLocation, setCurrentLocation,
    refreshLocations, setLocationAccessDenied,
  } = useAuth();
  const locationsRef = useRef(locations);
  locationsRef.current = locations;
  const prevLocationRef = useRef(currentLocation?.ghl_location_id);
  const syncCooldownRef = useRef(false);

  // App → GHL: When app location changes, notify parent iframe
  // Only send if NOT embedded via URL param (prevents redirect loops in CRM)
  useEffect(() => {
    const ghlLocId = currentLocation?.ghl_location_id;
    if (!ghlLocId || ghlLocId === prevLocationRef.current) return;
    prevLocationRef.current = ghlLocId;

    // Skip if we're in a cooldown (just received a sync FROM GHL)
    if (syncCooldownRef.current) return;

    // Don't send location-change to CRM if embedded via URL param — CRM controls location
    const params = new URLSearchParams(window.location.search);
    if (params.get('embedded') === '1' || params.get('loc')) return;

    try {
      if (window.self !== window.top) {
        window.parent.postMessage({
          source: 'optsc-app',
          action: 'change-location',
          ghlLocationId: ghlLocId,
        }, '*');
      }
    } catch {}
  }, [currentLocation?.ghl_location_id]);

  // GHL → App: Listen for messages from parent bridge
  const handleMessage = useCallback((event) => {
    const d = event.data;
    if (!d || d.source !== 'ghl-bridge') return;

    // Back navigation from CRM
    if (d.action === 'navigate-back') {
      window.history.back();
      return;
    }

    if (d.action !== 'location-sync') return;

    const ghlLocId = d.ghlLocationId;
    if (!ghlLocId) return;

    // Already on this location
    if (currentLocation?.ghl_location_id === ghlLocId) return;

    // Don't process if no user is logged in (prevents cross-user location leaks)
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    // Set cooldown to prevent bounce-back sync
    syncCooldownRef.current = true;
    setTimeout(() => { syncCooldownRef.current = false; }, 3000);

    // Find matching location in user's accessible locations
    const match = locationsRef.current.find(l => l.ghl_location_id === ghlLocId);
    if (match) {
      prevLocationRef.current = ghlLocId;
      setCurrentLocation(match);
      return;
    }

    // Location not in user's list — check if it exists but user lacks access
    refreshLocations().then(freshLocs => {
      const m = freshLocs.find(l => l.ghl_location_id === ghlLocId);
      if (m) {
        prevLocationRef.current = ghlLocId;
        setCurrentLocation(m);
      } else {
        setLocationAccessDenied(true);
      }
    }).catch(() => {});
  }, [currentLocation?.ghl_location_id, setCurrentLocation, refreshLocations, setLocationAccessDenied]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    // On mount: set location from URL param ?loc=<ghl_location_id> (for CRM custom menu links)
    try {
      const params = new URLSearchParams(window.location.search);
      const locParam = params.get('loc');
      if (locParam) {
        const match = locationsRef.current.find(l => l.ghl_location_id === locParam || l.id === locParam || l.code === locParam);
        if (match && match.ghl_location_id !== currentLocation?.ghl_location_id) {
          setCurrentLocation(match);
        }
      }
    } catch {}

    // Also request location from parent (if embedded in iframe)
    try {
      if (window.self !== window.top) {
        window.parent.postMessage({ source: 'optsc-app', action: 'get-location' }, '*');
      }
    } catch {}

    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);
}
